import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Grid,
  TextField,
  Modal,
  Button,
  Box,
  Select,
  MenuItem,
  Tooltip,
  InputLabel,
  InputAdornment,
  IconButton,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import base64 from "base64-js";



const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const slcInfoSections = [
  {
    title: "Overview",
    color: "#1976D2",
    description: "The SLC Management module manages School Leaving Certificates for students. You can view, generate PDF, and delete SLC records from this screen.",
    fields: [
      "Displays all SLC records in a paginated table",
      "Supports real-time search by Student Name",
      "Filter records by Class using the dropdown",
      "Authorization-based access control for all actions",
    ],
  },
  {
    title: "Table Columns",
    color: "#7B1FA2",
    description: "Each record in the SLC table contains the following information.",
    fields: [
      "Student Name – Full name of the student",
      "Roll No – Student's roll number",
      "Class – Class the student belonged to",
      "Issued Date – Date the SLC was issued",
      "Leaving Date – Date the student left the institution",
      "Actions – View (PDF) and Delete buttons per record",
    ],
  },
  {
    title: "Create SLC",
    color: "#388E3C",
    description: "Click the 'Create SLC' button to navigate to the SLC creation form and generate a new leaving certificate.",
    fields: [
      "Requires 'Write' permission (uWrite = 1)",
      "Navigates to /leavingcertif route",
      "Button is disabled and shows tooltip if not authorized",
    ],
  },
  {
    title: "View / Print SLC",
    color: "#F57C00",
    description: "Click the VIEW button on any row to generate and open the student's SLC as a PDF in a new browser tab.",
    fields: [
      "Requires 'Print' permission (uPrint = 1)",
      "Fetches SLC data from API by Student ID",
      "Generates a formatted A4 PDF with school logo, student details, and exam schedule",
      "PDF includes: Student Name, Father's Name, DOB, Admission Date, Leaving Date, Remarks, and Instructions",
      "Shows a loading dialog while the PDF is being generated",
    ],
  },
  {
    title: "Delete SLC",
    color: "#C62828",
    description: "Click the DELETE button to permanently remove a student's SLC. A confirmation modal appears before deletion.",
    fields: [
      "Requires 'Delete' permission (uDelete = 1)",
      "Shows a confirmation dialog with the student's name",
      "Button is disabled and shows tooltip if not authorized",
    ],
  },
  {
    title: "Search & Pagination",
    color: "#0277BD",
    description: "Use the search bar to filter SLC records by student name. Use the class dropdown to filter by class. Pagination controls let you navigate through large lists.",
    fields: [
      "Search filters by Student Name in real time",
      "Class dropdown filters records by selected class",
      "Rows per page: 5, 10, 25, 50",
      "Prev / Next buttons for page navigation",
      "Page count displayed as 'Page X of Y'",
    ],
  },
];

const SLCManagement = () => {
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canPrint, setCanPrint] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("0");
  const [ddlClass, setDdlClass] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedSLC, setSelectedSLC] = useState(null);
  const [slcData, setSlcData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // ── Info dialog state ──
  const [infoOpen, setInfoOpen] = useState(false);
  const navigate = useNavigate();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId, }), // replace with actual formId if hardcoded
      });

      const result = await response.json();
      const data = result[0];

      // setAuthorization(data);
      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
      setCanPrint(data?.uPrint === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };


  const handleGeneratePDF = async (studentId) => {
    const token = sessionStorage.getItem("token");
    const url = process.env.REACT_APP_BASE_URL;
    const apiUrl = `${url}/SLC/get-slc/${studentId}`;

    try {
      setIsPdfLoading(true);
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setSlcData(data);
      if (data && data.length > 0) {
        generatePDF(data);
      } else {
        console.error("No SLC data returned for the student");
      }
    } catch (error) {
      console.error("Error fetching the PDF:", error);
    }
  };

  const generatePDF = useCallback(async (data) => {
    // Helper function to convert date to words

    const fetchSchoolDetails = async () => {
  try {
    const token = sessionStorage.getItem("token");
    if (!token) throw new Error("No authentication token found");

    const response = await fetch('https://wsms.aidigitalsec.com/api/FeeReport/GetSchoolName', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status} ${response.statusText}`);
    const apiData = await response.json();
    const school = apiData[0] || {};

    return {
      schoolName: school.schoolName?.replace(/['"]+/g, '') || 'N/A',
      address: school.address?.replace(/['"]+/g, '') || '',
      phoneNo: school.phoneNo?.replace(/['"]+/g, '') || '',
    };
  } catch (error) {
    console.error("Error fetching school details:", error);
    return {
      schoolName: sessionStorage.getItem("organizationName")?.replace(/['"]+/g, '') || 'N/A',
      address: sessionStorage.getItem("address")?.replace(/['"]+/g, '') || '',
      phoneNo: sessionStorage.getItem("phoneNo")?.replace(/['"]+/g, '') || '',
    };
  }
};

    const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  // Handle both "2/10/2026 12:00:00 AM" and ISO "2026-02-10T00:00:00" formats
  return dateString.split(/[\sT]/)[0];
};

    const convertDateToWords = (dateStr) => {
      if (!dateStr || typeof dateStr !== 'string') return "N/A";

      const dateParts = dateStr.split(/[/\s:]+/);
      let day, month, year;

      if (dateParts.length > 3) {
        [month, day, year] = dateParts;
      } else {
        [day, month, year] = dateParts;
      }

      day = parseInt(day, 10);
      month = parseInt(month, 10);
      year = parseInt(year, 10);

      if (isNaN(day) || isNaN(month) || isNaN(year)) return "Invalid Date";

      const ones = ["", "First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth"];
      const tens = ["", "Tenth", "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth", "Sixteenth", "Seventeenth", "Eighteenth", "Nineteenth"];
      const twenties = ["", "", "Twentieth", "Thirtieth"];
      let dayInWords = "";
      if (day <= 19) {
        dayInWords = day < 10 ? ones[day] : tens[day - 10];
      } else {
        const dayTens = Math.floor(day / 10);
        const dayOnes = day % 10;
        dayInWords = (dayTens <= 3 ? twenties[dayTens] : ones[dayTens] + "ieth").replace("Fifth", "Fifth").replace("Ninth", "Ninth");
        if (dayOnes > 0) dayInWords = dayInWords.replace(/ieth$/, "") + "-" + ones[dayOnes].toLowerCase();
      }

      const months = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const monthInWords = months[month] || "Unknown Month";

      const yearToWords = (year) => {
        const numWords = {
          0: "Zero", 1: "One", 2: "Two", 3: "Three", 4: "Four", 5: "Five",
          6: "Six", 7: "Seven", 8: "Eight", 9: "Ninth", 10: "Ten",
          11: "Eleven", 12: "Twelve", 13: "Thirteen", 14: "Fourteen", 15: "Fifteen",
          16: "Sixteen", 17: "Seventeen", 18: "Eighteen", 19: "Nineteen",
          20: "Twenty", 30: "Thirty", 40: "Forty", 50: "Fifty",
          60: "Sixty", 70: "Seventy", 80: "Eighty", 90: "Ninety"
        };
        if (year < 20) return numWords[year];
        if (year < 100) {
          const tensPart = Math.floor(year / 10) * 10;
          const onesPart = year % 10;
          return onesPart ? `${numWords[tensPart]}-${numWords[onesPart].toLowerCase()}` : numWords[tensPart];
        }
        if (year < 1000) {
          const hundreds = Math.floor(year / 100);
          const remainder = year % 100;
          return remainder ? `${numWords[hundreds]} Hundred ${yearToWords(remainder)}` : `${numWords[hundreds]} Hundred`;
        }
        if (year < 10000) {
          const thousands = Math.floor(year / 1000);
          const remainder = year % 1000;
          return remainder ? `${numWords[thousands]} Thousand ${yearToWords(remainder)}` : `${numWords[thousands]} Thousand`;
        }
        return "Year Out of Range";
      };
      const yearInWords = yearToWords(year);

      return `${dayInWords} ${monthInWords} ${yearInWords}`;
    };

    try {
      const doc = new jsPDF("p", "mm", "a4");

      // Add custom font (fallback to Helvetica if font loading fails)
      doc.setFont("helvetica", "normal");

      // Add borders with rounded corners
      doc.setDrawColor(0, 51, 102); // Primary blue
      doc.setLineWidth(1.2);
      doc.roundedRect(8, 8, 194, 281, 3, 3); // Outer rounded border
      doc.setDrawColor(0, 102, 204); // Lighter blue
      doc.setLineWidth(0.4);
      doc.roundedRect(12, 12, 186, 273, 2, 2); // Inner rounded border

      // Add logo to PDF (if available)
      let logoDataUrl = null;
      let imageFormat = 'PNG';
      try {
        const token = sessionStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");
        const response = await fetch('https://wsms.aidigitalsec.com/api/FeeReport/GetSchoolName', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify({}),
        });
        if (!response.ok) throw new Error(`API error: ${response.status} ${response.statusText}`);
        const apiData = await response.json();
        const logoImg = apiData[0]?.logoImg;

        if (logoImg && typeof logoImg === 'string' && logoImg.trim() !== '') {
          let cleanedBase64 = logoImg
            .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
            .replace(/\s/g, '')
            .replace(/\n/g, '');
          const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
          cleanedBase64 += '='.repeat(paddingLength);

          const isValidBase64 = /^[A-Za-z0-9+/=]+$/.test(cleanedBase64);
          if (!isValidBase64) {
            console.error('Invalid base64 string for logoImg:', cleanedBase64.substring(0, 100) + '...');
            throw new Error('Invalid base64 string for logoImg');
          }

          // Log base64 for debugging
          console.log('Logo base64 (first 100 chars):', cleanedBase64.substring(0, 100) + '...');
          console.log('Full logo base64 length:', cleanedBase64.length);

          // Force PNG format
          imageFormat = 'PNG';
          logoDataUrl = `data:image/png;base64,${cleanedBase64}`;

          try {
          // Add larger white background to cover potential edge artifacts
doc.setFillColor(255, 255, 255);
doc.rect(14, 14, 35, 35, "F"); // Increased from 27x27 to 35x35 
// Add logo
doc.addImage(logoDataUrl, 'PNG', 15, 15, 33, 33); // Increased from 25x25 to 33x33

          } catch (error) {
            console.error('Error adding logo to PDF (PNG):', error.message);
            console.warn('The logo image may have an embedded grey background. Falling back to transparent placeholder.');
            // Fallback to a transparent 1x1 PNG
            const transparentPlaceholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
            doc.setFillColor(255, 255, 255);
            doc.rect(14, 14, 27, 27, "F");
            doc.addImage(transparentPlaceholder, 'PNG', 15, 15, 25, 25);
          }
        } else {
          console.warn('No valid logo image found. Using transparent placeholder.');
          // Fallback to a transparent 1x1 PNG
          const transparentPlaceholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
          doc.setFillColor(255, 255, 255);
          doc.rect(14, 14, 27, 27, "F");
          doc.addImage(transparentPlaceholder, 'PNG', 15, 15, 25, 25);
        }
      } catch (error) {
        console.error('Error fetching or processing logo:', error);
        console.warn('Using transparent placeholder due to logo fetch error.');
        // Fallback to a transparent 1x1 PNG
        const transparentPlaceholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
        doc.setFillColor(255, 255, 255);
        doc.rect(14, 14, 27, 27, "F");
        doc.addImage(transparentPlaceholder, 'PNG', 15, 15, 25, 25);
      }

      // School information
     // Fetch school details from API
const schoolDetails = await fetchSchoolDetails();

// Use schoolDetails instead of sessionStorage
const schoolName = schoolDetails.schoolName;
const schoolAddress = schoolDetails.address;
const contactInfo = schoolDetails.phoneNo;
const contactInfoWithLabel = `Phone No: ${contactInfo}`;


      // School Name
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(0, 51, 102);
      const maxSchoolNameWidth = 140;
      const splitSchoolName = doc.splitTextToSize(schoolName, maxSchoolNameWidth);

      // Calculate position for (Registered)
      const schoolNameWidth = doc.getTextWidth(splitSchoolName[0]);
      const registeredX = 58 + schoolNameWidth + 2; // 2 units padding after school name

      // Draw school name
      doc.text(splitSchoolName, 55, 22);

      // Add (Registered) in lighter style
      doc.setFontSize(12); // Smaller size
      doc.setTextColor(100, 100, 100); // Gray color
      doc.text("(Recognized)", registeredX, 22);

      // Reset styles
      doc.setFontSize(22);
      doc.setTextColor(0, 51, 102);

      const schoolNameHeight = splitSchoolName.length * 8;

      // Rest of your existing code remains exactly the same...
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);
      const addressStartX = 55;
      const maxAddressWidth = 140;
      const splitAddressText = doc.splitTextToSize(schoolAddress, maxAddressWidth);
      const addressLine1 = splitAddressText[0] || "";
      const addressLine2 = splitAddressText[1] || "";
      const addressY1 = 20 + schoolNameHeight + 3;
      const addressY2 = addressY1 + 5;
      doc.text(addressLine1, addressStartX, addressY1);
      doc.text(addressLine2, addressStartX, addressY2);

      // Contact Number (Moved to left)
      doc.setFontSize(11);
      const contactY = addressY2 + 6;
      doc.text(contactInfoWithLabel, 55, contactY, { align: "left" });
      // Decorative divider
      doc.setDrawColor(0, 51, 102);
      doc.setLineWidth(0.3);
      const dividerY = contactY + 10; // Increased clearance to avoid logo overlap
      doc.setFillColor(0, 51, 102, 0.2);
      doc.rect(20, dividerY - 1, 170, 1, "F"); // Subtle filled rectangle for divider

      // Title
      doc.setFontSize(30);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 51, 102);
      const titleY = dividerY + 15;
      doc.text("School Leaving Certificate", 105, titleY, { align: "center" });

      // Sub-title
      doc.setFontSize(13);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(50, 50, 50);
      const subtitleY = titleY + 8;
      doc.text("This certifies that the following student has left the institution:", 105, subtitleY, { align: "center" });

      // TC Number
      const tcNo = data && data.length > 0 ? `TC.NO: ${data[0].slcId}` : "TC.NO: N/A";
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 51, 102);
      const tcNoY = subtitleY + 6;
      doc.text(tcNo, 18, tcNoY);

      // TC Number underline
      doc.setDrawColor(0, 51, 102);
      doc.setLineWidth(0.2);
      const tcNoLineY = tcNoY + 2;
      doc.line(15, tcNoLineY, 41, tcNoLineY);

      // Content
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      let contentY = tcNoLineY + 12;

      if (data && data.length > 0) {
        const slc = data[0];
        const dobInWords = convertDateToWords(slc.studentDob);

        // Check if client is IDEAL to conditionally hide Dues Amount
        const clientCode = (sessionStorage.getItem("clientCode") || "").trim().toUpperCase();
        const isIDEAL = clientCode === "IDEAL";

        // Removed: Religion, Category (casteName), Reason for Leaving
        const details = [
          { label: "Student Name", value: slc.studentName },
          { label: "Admission No", value: slc.admissionNo },
          { label: "Aadhar No.", value: slc.adhaarNo },
          { label: "Father's Name", value: slc.fathersName },
          { label: "Mother's Name", value: slc.mothersName },
          { label: "Student DOB", value: slc.studentDob },
          { label: "in words", value: dobInWords },
          { label: "Place of Birth", value: "" },
          { label: "Address", value: slc.address },
          { label: "Admission Date", value: slc.admissionDate },
          { label: "Leaving Date", value: formatDate(slc.leavingDate) },
          { label: "Last Class Attended", value: slc.lastClassAttended },
          { label: "Remarks", value: slc.remarks },
          ...(!isIDEAL ? [{ label: "Dues Amount", value: slc.duesAmount }] : []),
          { label: "Issued Date", value: formatDate(slc.issuedDate) },
        ];

        const maxWidth = 120;
        const pageHeight = 277;
        const bottomMargin = 25;

        details.forEach(({ label, value }) => {
          const valueStr = value !== undefined && value !== null ? String(value) : "N/A";

          if (contentY + 12 > pageHeight - bottomMargin) {
            doc.addPage();
            contentY = 15;
            doc.setDrawColor(0, 51, 102);
            doc.setLineWidth(1.2);
            doc.roundedRect(8, 8, 194, 281, 3, 3);
            doc.setDrawColor(0, 102, 204);
            doc.setLineWidth(0.4);
            doc.roundedRect(12, 12, 186, 273, 2, 2);
          }

          doc.setFont("helvetica", "bold");
          doc.text(`${label}:`, 15, contentY);

          if (label === "Place of Birth") {
            let currentX = 65;
            const y = contentY;
            doc.setFont("helvetica", "normal");
            const cityName = slc.cityName && slc.cityName.trim() !== "" ? slc.cityName : "N/A";
            doc.text(cityName, currentX, y);
            currentX += doc.getTextWidth(cityName) + 4;
            doc.setFont("helvetica", "bold");
            doc.text("State:", currentX, y);
            const stateLabelWidth = doc.getTextWidth("State:");
            doc.setFont("helvetica", "normal");
            const stateName = slc.stateName && slc.stateName.trim() !== "" ? slc.stateName : "N/A";
            doc.text(stateName, currentX + stateLabelWidth, y);
            currentX += stateLabelWidth + doc.getTextWidth(stateName) + 6; // Increased gap to 6mm
            doc.setFont("helvetica", "bold");
            doc.text("Country:", currentX, y);
            const countryLabelWidth = doc.getTextWidth("Country:");
            doc.setFont("helvetica", "normal");
            const countryName = slc.countyName && slc.countyName.trim() !== "" ? slc.countyName : "N/A";
            doc.text(countryName, currentX + countryLabelWidth, y);
            currentX += countryLabelWidth + doc.getTextWidth(countryName) + 4;
            doc.setFont("helvetica", "bold");
            doc.text("Nationality:", currentX, y);
            const nationalityLabelWidth = doc.getTextWidth("Nationality:");
            doc.setFont("helvetica", "normal");
            const nationality = slc.nationality && slc.nationality.trim() !== "" ? slc.nationality : "N/A";
            doc.text(nationality, currentX + nationalityLabelWidth, y);
          } else {
            doc.setFont("helvetica", "normal");
            const splitText = doc.splitTextToSize(valueStr, maxWidth);
            doc.text(splitText, 65, contentY);
            contentY += (splitText.length - 1) * 6;
          }

          contentY += 8; // Tighter spacing for cleaner look
        });

        // Footer
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(60, 60, 60);
        doc.text('Certified that, above information is in accordance with the School Register.', 105, 236, { align: "center" });
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const penaltyText = '(No change in any entry shall be made except by the authority issuing it and';
        const penaltyText2 = 'any infringement of the requirement is liable to involve the imposition of penalty such as that of rustication)';
        const splitPenaltyText = doc.splitTextToSize(penaltyText, 170);
        const splitPenaltyText2 = doc.splitTextToSize(penaltyText2, 170);
        doc.text(splitPenaltyText, 105, 244, { align: "center" });
        doc.text(splitPenaltyText2, 105, 249, { align: "center" });

        doc.setFontSize(11);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(0, 51, 102);
        doc.text("Principal Signature", 145, 270);
        doc.setDrawColor(0, 51, 102);
        doc.setLineWidth(0.2);
        doc.line(135, 265, 185, 265);
      } else {
        console.error("Data array is empty or not defined");
      }

      // Output PDF
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setIsPdfLoading(false);
      window.open(pdfUrl, "_blank");
      // Save the PDF
      // doc.save("slc.pdf");
    } catch (error) {
      setIsPdfLoading(false);
      alert("Error Generating PDF")
      console.error("Error generating the PDF:", error);
    }
  }, []);

  const fetchClass = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching classes: ${response.status}`);
      }
      const data = await response.json();
      setDdlClass(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setDdlClass([]);
    }
  }, []);

  const fetchTableData = useCallback(async (classId) => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/SLC/GetAllSLCs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          classId: classId || 0,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error fetching SLC data: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setTableData(data);
      } else if (data.data && Array.isArray(data.data)) {
        setTableData(data.data);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error(error);
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClass();
    fetchTableData();
  }, [fetchClass, fetchTableData]);

  useEffect(() => {
    fetchTableData(selectedClass);
    Authorizer();
  }, [selectedClass, fetchTableData]);

  const filteredTableData = Array.isArray(tableData)
    ? tableData.filter((row) =>
      row.studentName?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : [];

  const handleDelete = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/SLC/Id?Id=${selectedSLC.studentId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    });
    if (response.ok) {
      setSnackbarMessage('SLC deleted successfully!');
      setOpenSnackbar(true);
    }
    if (!response.ok) {
      throw new Error(`Error deleting SLC: ${response.status}`);
    }
    setTableData((prevData) =>
      prevData.filter((item) => item.studentId !== selectedSLC.studentId)
    );
    setOpen(false);
  } catch (error) {
    console.error(error);
  }
};


  const handleDeleteClick = (slc) => {
    setSelectedSLC(slc);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCreateClick = () => {
    navigate("/leavingcertif");
  };

  const handleChangePage = (event, newPage) => {
  setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
  setRowsPerPage(parseInt(event.target.value, 10));
  setPage(1); // reset to page 1
};

const paginatedData = filteredTableData.slice(
  (page - 1) * rowsPerPage,
  page * rowsPerPage
);

  return (
    <Container maxWidth="lg" style={{ marginTop: "30px" }}>
      <Box
        sx={{
          backgroundColor: '#0B1F40', // dark navy blue
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          width: "100%",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold', flexGrow: 1 }}>
          SLC Management
        </Typography>

        {/* ── Info Button ── */}
        <IconButton
          onClick={() => setInfoOpen(true)}
          title="Page Information"
          style={{
            backgroundColor: "rgba(255,255,255,0.15)",
            border: "2px solid rgba(255,255,255,0.6)",
            borderRadius: "50%",
            padding: "6px",
            color: "#fff",
          }}
        >
          <InfoIcon style={{ fontSize: 26 }} />
        </IconButton>
      </Box>

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 0 } }}
      >
        <DialogTitle
          style={{
            backgroundColor: "#0B1F40",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              SLC Management — Page Guide
            </Typography>
          </Box>
         
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            This page manages School Leaving Certificates (SLC) for students. Below is a section-by-section guide to help you understand each feature.
          </Typography>

          {slcInfoSections.map((section, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: "#fff",
                borderRadius: 12,
                padding: "18px 20px",
                marginBottom: 16,
                borderLeft: `5px solid ${section.color}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", color: section.color, marginBottom: 6 }}
              >
                {section.title}
              </Typography>

              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                {section.description}
              </Typography>

              <Divider style={{ marginBottom: 10 }} />

              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 6 }}>
                📝 DETAILS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {section.fields.map((field, i) => (
                  <Chip
                    key={i}
                    label={field}
                    size="small"
                    variant="outlined"
                    style={{
                      borderColor: section.color,
                      color: section.color,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            style={{ backgroundColor: "#0B1F40", color: "#fff", borderRadius: 20, paddingInline: 28 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} style={{ padding: "20px", marginTop: 6, }}>

        <Grid
          container
          spacing={2}
          justifyContent="space-between"  // This creates space between elements
          alignItems="center"  // Vertically centers all items
          style={{ marginBottom: "15px" }}
        >
          {/* Left side group (search + dropdown) */}
          <Grid item container xs={12} sm={8} spacing={2} wrap="nowrap">
            <Grid item xs={8}>
              <TextField
                type="text"
                id="search"
                placeholder="Search"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  label="Class"
                >
                  <MenuItem value="0">
                    <em>Select Class</em>
                  </MenuItem>
                  {ddlClass.map((item) => (
                    <MenuItem key={item.classId} value={item.classId}>
                      {item.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Button pushed to the end */}
          <Grid item xs={12} sm="auto" style={{ textAlign: 'center' }}>
            <Tooltip title={!canAdd && "You are not Authorized to Create"} arrow><span>

              <Button
                variant="contained"
                color="primary"
                style={{
                  width: "116px",
                  height: "50px",
                  fontWeight: 'bold',
                }}
                onClick={handleCreateClick}
                disabled={!canAdd}
              >
                Create SLC
              </Button></span></Tooltip>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table
            sx={{
              minWidth: 650,
              border: '1px solid #ccc',
              '& .MuiTableCell-root': {
                borderRight: '1px solid #ccc',
                fontSize: '1rem',
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Student Name
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Roll No
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Class
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Issued Date
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  Leaving Date
                </TableCell>
                {/* Removed: Reason for Leaving column */}
                <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: "center" }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredTableData.length > 0 ? (
                paginatedData.map((row) => (
                  <TableRow key={row.studentId} sx={{ borderTop: '1px solid #ccc' }}>
                    <TableCell sx={{ fontSize: '1.1rem' }}>{row.studentName}</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem' }}>{row.rollNo}</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem' }}>{row.className}</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem' }}>
                      {new Date(row.issuedDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: '1.1rem' }}>
                      {new Date(row.leavingDate).toLocaleDateString()}
                    </TableCell>
                    {/* Removed: Reason for Leaving cell */}
                    <TableCell sx={{ fontSize: '1.1rem', textAlign: 'center' }}>
                      <Tooltip title={!canPrint && "You are not Authorized to View"} arrow><span>
                        <Button
                          variant="contained"
                          color="warning"
                          size="small"
                          sx={{
                            marginRight: 1,
                            fontSize: '0.875rem',
                            padding: '6px 12px',
                            textTransform: 'none'
                          }}
                          onClick={() => handleGeneratePDF(row.studentId)}
                          disabled={!canPrint}
                        >
                          View
                        </Button></span></Tooltip>
                      <Tooltip title={!canDelete && "You are not Authorized to Delete"} arrow><span>
                       <Button
  variant="contained"
  color="error"
  size="small"
  sx={{
    fontSize: '0.875rem',
    padding: '6px 12px',
    textTransform: 'none'
  }}
  onClick={() => handleDeleteClick(row)}
  disabled={!canDelete}
>
  Delete
</Button>
</span></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", fontSize: '1.1rem' }}>
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredTableData.length > 0 && (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
    <TextField
      select
      label="Rows per page"
      value={rowsPerPage}
      onChange={handleChangeRowsPerPage}
      sx={{ width: 150 }}
      SelectProps={{
        native: true,
      }}
    >
      {rowsPerPageOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </TextField>

    <Box>
      <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
        Page {page} of {Math.ceil(filteredTableData.length / rowsPerPage)}
      </Typography>
      <Button
        variant="outlined"
        onClick={() => handleChangePage(null, page - 1)}
        disabled={page === 1}
        sx={{ mr: 1 }}
      >
        Prev
      </Button>
      <Button
        variant="outlined"
        onClick={() => handleChangePage(null, page + 1)}
        disabled={page >= Math.ceil(filteredTableData.length / rowsPerPage)}
      >
        Next
      </Button>
    </Box>
  </Box>
)}

      </Paper>

      <Modal open={open} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedSLC?.studentName}'s</strong> SLC?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDelete(handleDelete)}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog
  open={isPdfLoading}
  PaperProps={{
    style: { background: "transparent", boxShadow: "none", overflow: "hidden" },
  }}
  sx={{ "& .MuiDialog-container": { alignItems: "center", justifyContent: "center" } }}
>
  <DialogContent
    sx={{ background: "white", padding: "20px", borderRadius: "8px", textAlign: "center", minWidth: "200px" }}
  >
    <div style={{
      width: "40px", height: "40px", border: "4px solid #f3f3f3",
      borderTop: "4px solid #3498db", borderRadius: "50%",
      animation: "spin 1s linear infinite", margin: "0 auto 15px",
    }} />
    <Typography>Generating PDF, please wait...</Typography>
  </DialogContent>
</Dialog>
    </Container>
  );
};

export default SLCManagement;
// {selectedSLC?.studentName}'s